import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trophy, Swords, Disc, ShieldAlert, Zap } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [careerStats, setCareerStats] = useState([]);
  const [selectedSeasonIdx, setSelectedSeasonIdx] = useState(0);
  const racer = user?.racer_profile;

  useEffect(() => {
    if (racer) {
      setForm({ ...racer });
      // Fetch career breakdown across seasons
      api.get(`/stats/racer/${racer.id}/seasons`)
        .then(res => setCareerStats(res.data || []))
        .catch(err => console.error("Error loading career stats:", err));
    }
  }, [racer?.id]);

  if (!user || user === false) return null;
  if (!racer) {
    return (
      <div className="max-w-2xl mx-auto p-16">
        <div className="rtrl-card carbon-bg p-8">
          <h1 className="font-display uppercase text-4xl text-white">No Racer Profile</h1>
          <p className="text-white/60 mt-2">Your account is not linked to a Racer record yet. A League Owner can link it from the Admin panel.</p>
        </div>
      </div>
    );
  }

  if (!form) return <div className="p-16 text-center small-label text-rtrl-silver">Accessing Flight Logs…</div>;

  const currentStats = careerStats[selectedSeasonIdx];

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/racers/${racer.id}`, {
        display_name: form.display_name,
        number: form.number ? parseInt(form.number, 10) : null,
        platform: form.platform,
        region: form.region,
        epic_id: form.epic_id,
        discord_username: form.discord_username || null,
        discipline: form.discipline,
      });
      await refresh();
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-16 space-y-12">
      <div>
        <div className="small-label text-rtrl-red">Racer</div>
        <h1 className="font-display uppercase text-5xl text-white mt-2">My Profile</h1>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Profile Settings Form */}
        <form onSubmit={save} className="lg:col-span-7 grid md:grid-cols-2 gap-6 rtrl-card carbon-bg p-8">
          <Field label="Racer Name"><input data-testid="prof-name" value={form.display_name || ""} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="rtrl-input" /></Field>
          <Field label="Number"><input data-testid="prof-number" type="number" value={form.number ?? ""} onChange={(e) => setForm({ ...form, number: e.target.value })} className="rtrl-input" /></Field>
          <Field label="Platform">
            <select data-testid="prof-platform" value={form.platform || ""} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="rtrl-input">
              <option value="">—</option><option>PC</option><option>Xbox</option><option>PlayStation</option>
            </select>
          </Field>
          <Field label="Region">
            <select data-testid="prof-region" value={form.region || ""} onChange={(e) => setForm({ ...form, region: e.target.value })} className="rtrl-input">
              <option value="">—</option><option>NA-West</option><option>NA-East</option><option>EU</option><option>APAC</option><option>SA</option>
            </select>
          </Field>
          <Field label="Epic Games ID">
            <input data-testid="prof-epic" value={form.epic_id || ""} onChange={(e) => setForm({ ...form, epic_id: e.target.value })} placeholder="For crossplay linking" className="rtrl-input" />
          </Field>
          <Field label="Discord Tag">
            <div className="flex gap-2">
              <input data-testid="prof-discord" value={form.discord_username || ""} onChange={(e) => setForm({ ...form, discord_username: e.target.value })} placeholder="e.g. jango_fett#1138" className="rtrl-input flex-1" />
              {form.discord_username ? (
                <span className="text-[10px] bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-2 flex items-center rounded uppercase font-mono-tech">Linked</span>
              ) : (
                <span className="text-[10px] bg-white/5 text-white/30 border border-white/10 px-2 flex items-center rounded uppercase font-mono-tech">Offline</span>
              )}
            </div>
          </Field>
          <Field label="Discipline" className="md:col-span-2">
            <select data-testid="prof-discipline" value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} className="rtrl-input">
              <option value="pod">Pod only</option><option value="speeder">Speeder only</option><option value="both">Both (Dual)</option>
            </select>
          </Field>
          <div className="md:col-span-2 pt-4">
            <button data-testid="prof-save" className="rtrl-btn">Save Profile</button>
          </div>
        </form>

        {/* Live Career stats display on the right */}
        <div className="lg:col-span-5 space-y-6">
          <div className="rtrl-card carbon-bg p-6 border border-rtrl-red/25 relative overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
            <div className="scanline" />

            <div className="relative z-10 space-y-6">
              <div className="flex items-center justify-between">
                <span className="small-label text-rtrl-silver font-semibold">Career Records:</span>
                {careerStats.length > 0 && (
                  <select
                    className="bg-void border border-surface-line px-2 py-1 text-xs text-white outline-none rounded font-mono-tech"
                    value={selectedSeasonIdx}
                    onChange={(e) => setSelectedSeasonIdx(parseInt(e.target.value, 10))}
                    data-testid="profile-season-select"
                  >
                    {careerStats.map((cs, idx) => (
                      <option key={cs.season.id} value={idx}>{cs.season.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {currentStats ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="bg-void/40 p-2 border border-surface-line/20 rounded">
                      <div className="text-[10px] text-white/40 uppercase font-mono-tech">PTS</div>
                      <div className="text-white font-display text-2xl font-bold mt-0.5">{currentStats.points}</div>
                    </div>
                    <div className="bg-void/40 p-2 border border-surface-line/20 rounded">
                      <div className="text-[10px] text-white/40 uppercase font-mono-tech">Wins</div>
                      <div className="text-emerald-400 font-display text-2xl font-bold mt-0.5">{currentStats.wins}</div>
                    </div>
                    <div className="bg-void/40 p-2 border border-surface-line/20 rounded">
                      <div className="text-[10px] text-white/40 uppercase font-mono-tech">Podiums</div>
                      <div className="text-amber-400 font-display text-2xl font-bold mt-0.5">{currentStats.podiums}</div>
                    </div>
                    <div className="bg-void/40 p-2 border border-surface-line/20 rounded">
                      <div className="text-[10px] text-white/40 uppercase font-mono-tech">DNFs</div>
                      <div className="text-rtrl-red font-display text-2xl font-bold mt-0.5">{currentStats.dnfs}</div>
                    </div>
                  </div>

                  {/* Individual race history inside profile stats card */}
                  <div className="space-y-1.5">
                    <div className="text-[10px] text-white/40 uppercase font-mono-tech pb-1 border-b border-surface-line/10">Race History ({currentStats.races} races)</div>
                    <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1">
                      {currentStats.results.map((r, rIdx) => (
                        <div key={rIdx} className="flex justify-between items-center bg-void/10 border border-surface-line/10 px-2.5 py-1.5 rounded text-xs">
                          <span className="text-white/60 truncate font-mono-tech max-w-[150px]">R{r.round_number} Race {r.race_number} · {r.track_name}</span>
                          <div className="flex items-center gap-2 font-bold font-mono-tech shrink-0">
                            <span className={r.dnf ? "text-rtrl-red" : r.position === 1 ? "text-amber-400" : "text-white"}>
                              {r.dnf ? "DNF" : `P${r.position}`}
                            </span>
                            <span className="text-white/30 text-[10px] font-normal">+{r.points}pts</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-white/40 italic text-sm">No points records logged in any past championships.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="small-label mb-2">{label}</div>{children}</label>;
}
